import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, Sparkle, Books, ArrowRight, Trash, Fire, CheckCircle, CalendarCheck } from "@phosphor-icons/react";

export default function Dashboard() {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({ total_completions: 0, current_streak: 0, active_days: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/plans"), api.get("/me/stats")])
      .then(([p, s]) => { setPlans(p.data); setStats(s.data); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Delete this plan?")) return;
    await api.delete(`/plans/${id}`);
    load();
  };

  return (
    <Layout>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <StatTile icon={Fire} label="Current Streak" value={stats.current_streak} suffix="days" testid="stat-streak" />
        <StatTile icon={CheckCircle} label="Total Sessions" value={stats.total_completions} suffix="completed" testid="stat-completions" />
        <StatTile icon={CalendarCheck} label="Active Days" value={stats.active_days} suffix="logged" testid="stat-active-days" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <ActionCard to="/builder" icon={Plus} label="New Plan" sub="Build microcycle from scratch" testid="dash-new-plan" />
        <ActionCard to="/ai" icon={Sparkle} label="AI Coach" sub="Generate mesocycle from goal" testid="dash-ai-coach" />
        <ActionCard to="/library" icon={Books} label="Exercise Library" sub="Browse drills & alternates" testid="dash-library" />
      </div>

      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono mb-1">// MY PLANS</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black">SAVED MISSIONS</h2>
        </div>
        <Link to="/builder" className="hidden sm:inline-flex items-center gap-2 border border-border px-4 py-2 uppercase text-xs tracking-widest hover:border-volt hover:text-volt" data-testid="dash-new-plan-secondary">
          <Plus size={14} weight="bold" /> New
        </Link>
      </div>

      {loading ? (
        <div className="label-mono animate-pulse">LOADING PLANS...</div>
      ) : plans.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <div className="label-mono mb-3">NO PLANS YET</div>
          <p className="text-muted-foreground mb-6">Create your first microcycle or let the AI coach draft a mesocycle.</p>
          <div className="flex justify-center gap-3">
            <Link to="/builder" className="bg-volt text-black px-5 py-3 font-display uppercase tracking-widest text-sm">Build Manually</Link>
            <Link to="/ai" className="border border-border px-5 py-3 font-display uppercase tracking-widest text-sm hover:border-volt hover:text-volt">Use AI Coach</Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => {
            const done = Object.keys(p.completions || {}).length;
            const totalDays = (p.weeks || []).reduce((a, w) => a + (w.days?.length || 0), 0);
            return (
              <div key={p.id} className="tactical-card p-5 flex flex-col h-full" data-testid={`plan-card-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="label-mono text-volt">{p.type === "mesocycle" ? "MESOCYCLE" : "MICROCYCLE"}</div>
                    {p.is_public && <div className="label-mono px-1.5 py-0.5 bg-volt/20 text-volt border border-volt">PUBLIC</div>}
                  </div>
                  <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-plan-${p.id}`}><Trash size={16} weight="bold" /></button>
                </div>
                <h3 className="font-display text-2xl font-black leading-tight mb-2">{p.name}</h3>
                <div className="text-xs text-muted-foreground mb-4 space-y-1">
                  {p.goal && <div>GOAL: {p.goal}</div>}
                  {p.level && <div>LEVEL: {p.level}</div>}
                  <div>WEEKS: {p.weeks?.length || 0} · COMPLETED: {done}/{totalDays}</div>
                </div>
                <Link to={`/plans/${p.id}`} className="mt-auto inline-flex items-center justify-between border border-border px-3 py-2 hover:border-volt hover:text-volt text-xs uppercase tracking-widest" data-testid={`open-plan-${p.id}`}>
                  Log Workouts <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}

function StatTile({ icon: Icon, label, value, suffix, testid }) {
  return (
    <div className="tactical-card p-5 flex items-center gap-4" data-testid={testid}>
      <div className="w-12 h-12 grid place-items-center bg-secondary border border-border">
        <Icon size={22} weight="bold" className="text-volt" />
      </div>
      <div>
        <div className="label-mono">{label}</div>
        <div className="font-display text-3xl font-black leading-none">{value} <span className="text-base text-muted-foreground font-normal">{suffix}</span></div>
      </div>
    </div>
  );
}

function ActionCard({ to, icon: Icon, label, sub, testid }) {
  return (
    <Link to={to} className="tactical-card p-6 flex items-center gap-5 group" data-testid={testid}>
      <div className="w-14 h-14 grid place-items-center bg-secondary border border-border group-hover:bg-volt group-hover:text-black transition-colors">
        <Icon size={26} weight="bold" />
      </div>
      <div>
        <div className="font-display text-2xl font-black uppercase">{label}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
    </Link>
  );
}
